import { useState, useEffect, useCallback, useRef } from "react";
import { Node, Edge, useReactFlow, useNodesInitialized, NodeChange, addEdge } from "reactflow";
import metaStoreService from "core/services/metaStoreService/meta-store-service";
import appStatusSynchronizer from "core/appStatusSynchronizer";
import api from "core/api/api";
import navigateTo from "src/navigate-to";
import growl from "app/components/common/growl";
import dialog from "app/components/common/dialogs/dialogWindow";
import App from "app";
import MetaObjectConverter from "core/services/metaStoreService/metaobject-converter";
import {
    getExistingNodes,
    createEdgesFromOutputStreams,
    createEdgesFromComponentData,
    getLayoutedElements,
    Layouts,
    type Layout,
    updateEdgesWithStatus,
    highlightConnectedEdges
} from "./flow.utils";
import { useAppDispatch, useAppSelector } from "src/stores/redux/hooks";
import {
    getAppFQN,
    getAppModel,
    getAppStatus,
    getCurrentFlowId,
    getCurrentLayout,
    getCurrentPlacementAlgorithm,
    getEdges,
    getIsAppErrorsSidebarOpen,
    getNodes,
    getSelectedNode
} from "./selectors";
import {
    setAppModelAction,
    setAppStatusAction,
    closeRightSidebarAction,
    setCurrentLayoutAction,
    setEdgesAction,
    setIsRightSidebarOpenAction,
    setIsFetchingNodesAction,
    setIsEmptyAction,
    setNodesAction,
    setSelectedNodeAction,
    setPlacementAlgorithmAction,
    closeAllSidebarsAction,
    setShowAppErrorsSidebarAction,
    setLoadLocalErrorsAction
} from "src/stores/redux/features/flowdesigner/flowdesigner.slice";
import localErrorsStorage from "app/components/flow/designer/appErrors/localErrorsStorage";
import statusManagement from "src/status-management";
import store from "../../../../stores/redux/store";
import { getArrangedNodes, moveFirstNodeToCenter } from "./layout.utils";
import { PlacementAlgorithm } from "src/stores/redux/features/flowdesigner/flowdesignerslice.types";
import { clearByApplication, saveAlgAndLayout, savePosition } from "app/components/flow/designer/nodesPositionStorage";
import { navigateToNode, navigateToFlow } from "./navigation.utils";
import { getAutoPropertiesForStreamChild } from "../add-new-node/add-new-node.utils";
import { isTempNode, setSavedSelection } from "./node.utils";
import { EdgeType } from "../flow/flow.constants";
import metaobjectConverter from "core/services/metaStoreService/metaobject-converter";
import { getAppComponents } from "./flow-data.utils";

// Maps a dropped component type to the node types it can be connected to
export const ALLOWED_PARENT_TYPES: Record<string, string[]> = {
    TARGET: ["STREAM"],
    WINDOW: ["STREAM"],
    ROUTER: ["STREAM"],
    SENTINEL: ["STREAM"],
    CQ: ["STREAM", "WINDOW", "CACHE", "WACTIONSTORE"],
    OPENPROCESSOR: ["STREAM", "CACHE"],
    WACTIONSTORE: ["CQ"]
};

/**
 * Returns the ReactFlow node that is visually under the given screen coordinates,
 * or null if no node is found there.
 */
const getNodeAtDropPosition = (x: number, y: number, nodes: Node[]): Node | null => {
    const elements = document.elementsFromPoint(x, y);
    for (const el of elements) {
        const nodeEl = el.closest(".react-flow__node") as HTMLElement | null;
        if (nodeEl) {
            const nodeId = nodeEl.getAttribute("data-id");
            if (nodeId) {
                return nodes.find(n => n.id === nodeId) || null;
            }
        }
    }
    return null;
};

/**
 * Defers a `moveFirstNodeToCenter` call until ReactFlow has measured the nodes,
 * so callers don't need to guess a setTimeout delay after dispatching new nodes.
 */
const useDeferredRecenter = () => {
    const { setCenter, fitView } = useReactFlow();
    const nodesInitialized = useNodesInitialized();
    const [pendingCenter, setPendingCenter] = useState<{ nodes: Node[]; direction: Layout } | null>(null);

    useEffect(() => {
        if (nodesInitialized && pendingCenter) {
            const { nodes, direction } = pendingCenter;
            setPendingCenter(null);
            moveFirstNodeToCenter(nodes, setCenter, fitView, direction);
        }
    }, [nodesInitialized, pendingCenter, setCenter, fitView]);

    return useCallback((nodes: Node[], direction: Layout) => {
        setPendingCenter({ nodes, direction });
    }, []);
};

/**
 * Hook to manage flow data fetching and population
 */
export const useFetchAndPopulateNodes = () => {
    const dispatch = useAppDispatch();
    const appFQN = useAppSelector(getAppFQN);
    const currentFlowId = useAppSelector(getCurrentFlowId);
    const currentLayout = useAppSelector(getCurrentLayout);
    const currentPlacementAlgorithm = useAppSelector(getCurrentPlacementAlgorithm);

    const requestRecenter = useDeferredRecenter();
    const abortControllerRef = useRef<AbortController | null>(null);

    return useCallback(
        async ({ skipReCenter = false } = {}) => {
            // Cancel any previous fetch
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            // Create new abort controller for this fetch
            abortControllerRef.current = new AbortController();
            const signal = abortControllerRef.current.signal;
            let layoutedNodesResult: Node[] = [];

            if (!appFQN || !currentFlowId) {
                return;
            }

            dispatch(setIsFetchingNodesAction(true));

            try {
                // Check if fetch was cancelled before starting
                if (signal.aborted) return;
                const app = await metaStoreService.findById(appFQN);
                // Check if fetch was cancelled after app fetch
                if (signal.aborted) return;

                // Check if application was not found (same behavior as old flow designer)
                if (!app) {
                    const applicationName = MetaObjectConverter.convertFullNameToShortName(appFQN);
                    growl.error(`The application ${applicationName} was not found`, "App not found");
                    navigateTo.Apps();
                    return;
                }

                dispatch(setAppModelAction(app));
                const appComponents = await getAppComponents(appFQN);             
                const existingNodes = await getExistingNodes(appFQN, appComponents, currentFlowId);
                // Check if fetch was cancelled after getting nodes
                if (signal.aborted) return;

                if (existingNodes && existingNodes.length > 0) {
                    // Determine if we're viewing a specific flow or the main app view
                    const isViewingSpecificFlow = currentFlowId ? currentFlowId !== appFQN : false;

                    // Try the new async edge creation for flow-to-flow connections first
                    let existingEdges: Edge[];
                    try {
                        existingEdges = await createEdgesFromComponentData(
                            appFQN,
                            existingNodes,
                            appComponents,
                            isViewingSpecificFlow
                        );
                    } catch (error) {
                        console.warn(
                            "Failed to create edges from component data, falling back to basic edge creation:",
                            error
                        );
                        existingEdges = createEdgesFromOutputStreams(existingNodes, isViewingSpecificFlow);
                    }
                    const { nodes: layoutedNodes, edges: layoutedEdges } = getArrangedNodes(
                        existingNodes,
                        existingEdges,
                        currentLayout,
                        currentPlacementAlgorithm,
                        true // use saved positions on initial load
                    );
                    layoutedNodesResult = layoutedNodes;
                    // Final check before setting nodes - this is critical to prevent race conditions
                    if (signal.aborted) return;
                    
                    dispatch(setNodesAction(layoutedNodes));
                    if (layoutedEdges.length > 0) {
                        dispatch(setEdgesAction(layoutedEdges));
                    }
                } else {
                    // Clear nodes and edges when no components are found (e.g., when viewing an empty flow)
                    dispatch(setIsEmptyAction(true));
                    dispatch(setNodesAction([]));
                    dispatch(setEdgesAction([]));
                }
            } catch (error) {
                // Don't log or clear nodes if the fetch was just cancelled
                if (error.name === "AbortError" || signal.aborted) {
                    return;
                }

                console.error("Failed to fetch application components:", error);
                // Don't clear nodes on error - this could cause nodes to disappear temporarily
                // Only clear if this is the initial load (no existing nodes)
                const currentNodes = getNodes(store.getState());
                if (!currentNodes || currentNodes.length === 0) {
                    dispatch(setNodesAction([]));
                    dispatch(setEdgesAction([]));
                }
            } finally {
                dispatch(setIsFetchingNodesAction(false));
                if (!signal.aborted && !skipReCenter && layoutedNodesResult.length > 0) {
                    requestRecenter(layoutedNodesResult, currentLayout);
                }
            }
        },
        [appFQN, currentFlowId, currentLayout, currentPlacementAlgorithm, dispatch, requestRecenter]
    );
};

/**
 * Hook to manage flow data fetching and population
 */
export const useFlowData = () => {
    const fetchAndPopulateNodes = useFetchAndPopulateNodes();

    useEffect(() => {
        (async () => {
            await fetchAndPopulateNodes();
        })();
    }, [fetchAndPopulateNodes]);

    return { fetchAndPopulateNodes };
};

/**
 * Hook to manage app status synchronization
 */
export const useAppStatus = () => {
    const appFQN = useAppSelector(getAppFQN);
    const appStatus = useAppSelector(getAppStatus);
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!appFQN) {
            dispatch(setAppStatusAction(null));
            return;
        }

        const initialStatus = appStatusSynchronizer.getStatus(appFQN);
        dispatch(setAppStatusAction(initialStatus));

        const handleStatusChange = ({ appId, newStatus }) => {
            if (appId !== appFQN) return;

            dispatch(setAppStatusAction(newStatus));

            const appModel = getAppModel(store.getState());
            if (!appModel) return;

            if (newStatus === "RUNNING") {
                localErrorsStorage.clear(appModel);
                return;
            }

            if (
                statusManagement.isApplicationCrash(newStatus) ||
                statusManagement.isApplicationFailedOnDeployment(newStatus)
            ) {
                dispatch(closeAllSidebarsAction());
                dispatch(setLoadLocalErrorsAction(false));
                dispatch(setShowAppErrorsSidebarAction(true));
                navigateTo.AppErrors(MetaObjectConverter.convertFullNameToShortName(appFQN));
            } else if (newStatus === "CREATED") {
                if (getIsAppErrorsSidebarOpen(store.getState())) {
                    const localErrors = localErrorsStorage.load(appModel);
                    if (localErrors && localErrors.length > 0) {
                        dispatch(setLoadLocalErrorsAction(true));
                    }
                }
            } else {
                const localErrors = localErrorsStorage.load(appModel);
                if (localErrors && localErrors.length > 0) {
                    dispatch(closeAllSidebarsAction());
                    dispatch(setLoadLocalErrorsAction(true));
                    dispatch(setShowAppErrorsSidebarAction(true));
                    navigateTo.AppErrors(MetaObjectConverter.convertFullNameToShortName(appFQN));
                }
            }
        };

        appStatusSynchronizer.on("status_change", handleStatusChange);

        return () => {
            appStatusSynchronizer.off("status_change", handleStatusChange);
        };
    }, [appFQN, dispatch]);

    useEffect(() => {
        if (!appStatus) return;
        const edges = getEdges(store.getState());
        const updatedEdges = updateEdgesWithStatus(edges, appStatus);

        if (updatedEdges !== edges) {
            dispatch(setEdgesAction(updatedEdges));
        }
    }, [appStatus, dispatch]);

    return appStatus;
};

/**
 * Simple polling hook for node statistics monitoring
 * Replicates the functionality from the old flow designer's rateCounter.js
 */
export const useNodeStatistics = (appStatus: string | null) => {
    const dispatch = useAppDispatch();
    const appFQN = useAppSelector(getAppFQN);

    useEffect(() => {
        // Only poll when app is running
        if (!appFQN || appStatus !== "RUNNING") {
            return;
        }

        const fetchAndUpdateStats = async () => {
            try {
                const stats = await api.getMonitoringStatsForAppWithoutTableInfo(appFQN);
                // Read current nodes from store to avoid stale closure
                const currentNodes = getNodes(store.getState());
                if (!currentNodes || currentNodes.length === 0) {
                    return; // Don't update if no nodes are present
                }

                const updatedNodes = currentNodes.map((node: Node) => {
                    const currentStat = Object.values(stats as Record<string, any>).find(
                        (stat: any) => stat.name === node.data.label
                    );
                    return { ...node, data: { ...node.data, stats: currentStat } };
                });

                // Only update if we have valid updated nodes
                if (updatedNodes && updatedNodes.length > 0) {
                    dispatch(setNodesAction(updatedNodes));
                }
            } catch (error) {
                console.error("Failed to fetch monitoring statistics:", error);
                // Don't clear nodes on statistics fetch error - just log and continue
            }
        };
        // Initial fetch
        fetchAndUpdateStats();
        // Set up 5-second interval
        const interval = setInterval(fetchAndUpdateStats, 5000);

        // Cleanup
        return () => {
            clearInterval(interval);
        };
    }, [appFQN, appStatus, dispatch]); // Removed nodes from dependencies to prevent effect re-running
};

/**
 * Hook to manage node selection and sidebar state
 */
export const useNodeSelection = (appName?: string, flowName?: string) => {
    const dispatch = useAppDispatch();
    const selectedNode = useAppSelector(getSelectedNode);
    const { getNode, setCenter } = useReactFlow();

    const onNodeClick = useCallback(
        (_event: React.MouseEvent, node: Node) => {
            // If node is already the selected node, Return
            if (selectedNode?.id === node.id) return;

            // Handle FLOW node clicks differently - navigate to the flow instead of opening sidebar
            // skip tooltip clicks
            if (_event?.target?.parentNode?.id === "striim-tooltip") return;
            if (node.data?.componentType === "FLOW" && appName) {
                const flowName = node.data?.metaObject?.name || node.data?.name;
                const isAppFlow = node.data?.isAppFlow;

                if (isAppFlow) {
                    // Navigate back to the main app view
                    navigateTo.App(appName, true);
                    return;
                } else if (flowName) {
                    navigateTo.Subflow(appName, flowName, true);
                    return;
                }
            }

            dispatch(closeAllSidebarsAction({ appName, flowName }));
            // For all other node types, open the sidebar and update URL
            dispatch(setSelectedNodeAction(node));
            dispatch(setIsRightSidebarOpenAction(true));

            // Update URL to reflect selected node (only for saved nodes, not temp nodes)
            // This matches the old flow designer behavior (see designer.js line 1270)
            const hasMetaObjectId = node.data?.metaObjectId && node.data?.metaObjectId !== "temp-node";
            const nodeName = node.data?.metaObject?.name || node.data?.name;
            if (hasMetaObjectId && nodeName && appName) {
                navigateToNode(flowName, appName, nodeName);
                setSavedSelection(appName, nodeName);
            }
        },
        [appName, flowName, dispatch, selectedNode]
    );

    const closeRightSidebar = useCallback(() => {
        dispatch(closeRightSidebarAction({ appName, flowName }));
    }, [appName, flowName, dispatch]);

    // Center the selected node when it changes
    useEffect(() => {
        if (selectedNode) {
            const node = getNode(selectedNode.id);
            if (node && node.position) {
                setTimeout(() => {
                    const nodeWidth = node?.width ?? 24;
                    const xPosition = node.position.x + nodeWidth / 2;
                    setCenter(xPosition, node.position.y, { zoom: 1, duration: 500 });
                }, 100);
            }
        }
    }, [selectedNode, getNode, setCenter]);

    return {
        selectedNode,
        onNodeClick,
        closeRightSidebar
    };
};

/**
 * Hook to save node position
 */

export const useSaveNodePosition = () => {
    const saveNodeposition = useCallback((changes: NodeChange[]) => {
        // if it is a flow node, don't save the position
        const event = changes[0];
        const metaObjectType = metaobjectConverter.getType(event.id);
        if (metaObjectType === "FLOW" || metaObjectType === "APPLICATION") return;
        const nodes = getNodes(store.getState());
        const appFQN = getAppFQN(store.getState());
        if (event.type === "position" && event.dragging === false && event.id) {
            const node = nodes.find(node => node.id === event.id);
            if (!node || !appFQN) return;
            savePosition(appFQN, event.id, { left: node.position.x, top: node.position.y });
        }
    }, []);
    return saveNodeposition;
};

/**
 * Hook to manage flow layout
 */
export const useFlowLayout = () => {
    const dispatch = useAppDispatch();
    const currentLayout = useAppSelector(getCurrentLayout);
    const { setCenter, fitView } = useReactFlow();

    const onLayout = useCallback(
        (direction: Layout = Layouts.VERTICAL, overrideAlgorithm: PlacementAlgorithm | null = null) => {
            const nodes = getNodes(store.getState());
            const edges = getEdges(store.getState());
            const currentPlacementAlgorithm = getCurrentPlacementAlgorithm(store.getState());
            const algorithm = overrideAlgorithm ?? currentPlacementAlgorithm;
            const { nodes: layoutedNodes, edges: layoutedEdges } = getArrangedNodes(nodes, edges, direction, algorithm);
            dispatch(setNodesAction([...layoutedNodes]));
            dispatch(setEdgesAction([...layoutedEdges]));
            
             setTimeout(() => {
                  moveFirstNodeToCenter(layoutedNodes, setCenter, fitView, direction);
     
                 const appFQN = getAppFQN(store.getState());
                 if (appFQN) {
                     saveAlgAndLayout(appFQN, algorithm, direction);
                     clearByApplication(appFQN);
                 }
             }, 0)

        },
        [dispatch, setCenter]
    );

    const onLayoutChange = useCallback(
        (direction: Layout) => {
            dispatch(setCurrentLayoutAction(direction));
            onLayout(direction);
        },
        [dispatch, onLayout]
    );

    const toggleLayout = useCallback(async () => {
        const confirmed = await dialog.confirm(
            "Changing the layout direction will reset all custom node position changes. Do you want to proceed?",
            App.Dashboards.ModalManager,
            "small",
            "Confirm Layout Change",
            "Confirm"
        );
        if (!confirmed) return;

        const newDirection: Layout = currentLayout === Layouts.VERTICAL ? Layouts.HORIZONTAL : Layouts.VERTICAL;
        onLayoutChange(newDirection);
    }, [currentLayout, onLayoutChange]);

    const refreshLayout = useCallback(() => {
        onLayout(currentLayout);
    }, [currentLayout, onLayout]);

    const onArrangementChange = useCallback(
        (algorithm: PlacementAlgorithm, direction?: Layout) => {
            dispatch(setPlacementAlgorithmAction(algorithm));
            const resolvedDirection = direction ?? currentLayout;
            if (direction && direction !== currentLayout) {
                dispatch(setCurrentLayoutAction(direction));
            }
            onLayout(resolvedDirection, algorithm);
        },
        [dispatch, currentLayout, onLayout]
    );

    return { currentLayout, toggleLayout, refreshLayout, onArrangementChange };
};

/**
 * Hook to manage node search functionality
 */
export const useNodeSearch = () => {
    const dispatch = useAppDispatch();
    const { getNode } = useReactFlow();

    const handleNodeSearch = useCallback(
        (nodeId: string | null) => {
            if (!nodeId) return;

            const node = getNode(nodeId);
            if (node) {
                dispatch(setSelectedNodeAction(node));
                if (node.type !== "FLOW") dispatch(setIsRightSidebarOpenAction(true));
            }
        },
        [dispatch, getNode]
    );

    return handleNodeSearch;
};

/**
 * Hook to manage drag and drop functionality
 */
export const useDragAndDrop = (
    appName?: string,
    flowName?: string,
    promptIfUnsavedChanged?: () => Promise<boolean>
) => {
    const dispatch = useAppDispatch();
    const setNodes = useCallback((nodes: Node[]) => dispatch(setNodesAction(nodes)), [dispatch]);
    const setEdges = useCallback((edges: Edge[]) => dispatch(setEdgesAction(edges)), [dispatch]);

    const { screenToFlowPosition } = useReactFlow();
    const nodeIdCounter = useRef(0);

    const onDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
    }, []);

    const onDrop = useCallback(
        async (event: React.DragEvent<HTMLDivElement>, componentDnDDataTransfer: any) => {
            event.preventDefault();

            const nodes = getNodes(store.getState());
            const edges = getEdges(store.getState());

            const type = event.dataTransfer.getData(componentDnDDataTransfer.type);
            const name = event.dataTransfer.getData(componentDnDDataTransfer.name);
            const componentId = event.dataTransfer.getData(componentDnDDataTransfer.componentId);

            if (!type || !name || !componentId) {
                return;
            }

            // Detect whether the component was dropped on top of an existing node
            const targetNode = getNodeAtDropPosition(event.clientX, event.clientY, nodes);
            const targetNodeType = targetNode?.data?.componentType as string | undefined;
            const allowedParents = ALLOWED_PARENT_TYPES[type] || [];
            const isDropOnNode = !!(targetNode && targetNodeType && allowedParents.includes(targetNodeType));

            const position = isDropOnNode
                ? { x: targetNode.position.x + 150, y: targetNode.position.y + 130 }
                : screenToFlowPosition({ x: event.clientX, y: event.clientY });

            // When dropped on a valid parent, pre-fill connection properties (e.g. inputStream)
            const autoProperties = isDropOnNode
                ? getAutoPropertiesForStreamChild(type, targetNode.data?.metaObject, name)
                : {};

            const newNodeId = `node-${nodeIdCounter.current++}`;
            const newNode: Node = {
                id: newNodeId,
                type: type,
                position,
                data: {
                    label: name,
                    type: type,
                    componentType: type,
                    name: name,
                    componentId: componentId,
                    metaObjectId: "temp-node",
                    targets: [],
                    metaObject: {
                        type: type,
                        name: name,
                        ...(Object.keys(autoProperties).length > 0 ? { autoProperties } : {})
                    }
                }
            };

            const hasTempNode = nodes.some(n => isTempNode(n.data));
            if (hasTempNode) {
                const shouldProceed = promptIfUnsavedChanged ? await promptIfUnsavedChanged() : true;
                if (!shouldProceed) return;
            }

            const nodesWithoutTemp = nodes.filter(n => !isTempNode(n.data));

            if (isDropOnNode) {
                const updatedEdges = addEdge(
                    {
                        source: targetNode.id,
                        sourceHandle: null,
                        target: newNodeId,
                        targetHandle: null,
                        type: EdgeType
                    },
                    edges
                );
                setNodes([...nodesWithoutTemp, newNode]);
                setEdges(updatedEdges);
            } else {
                setNodes([...nodesWithoutTemp, newNode]);
            }

            // Clear any previously-saved node name from the URL so the stale path doesn't persist
            navigateToFlow(appName, flowName);
            // Small delay to ensure React has updated the nodes array before opening sidebar
            setTimeout(() => {
                dispatch(setSelectedNodeAction(newNode));
                dispatch(setIsRightSidebarOpenAction(true));
            }, 0);
            // URL navigation will happen after the node is saved
        },
        [dispatch, screenToFlowPosition, setNodes, setEdges, appName, flowName, promptIfUnsavedChanged]
    );

    return { onDragOver, onDrop };
};

/**
 * Hook to manage edge highlighting on node hover
 */
export const useEdgeHighlighting = (appStatus: string | null) => {
    const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
    const dispatch = useAppDispatch();
    const setEdges = useCallback((edges: Edge[]) => dispatch(setEdgesAction(edges)), [dispatch]);

    const onNodeMouseEnter = useCallback((_event: React.MouseEvent, node: Node) => {
        setHoveredNodeId(node.id);
    }, []);

    const onNodeMouseLeave = useCallback(() => {
        setHoveredNodeId(null);
    }, []);

    // Update edges when hovered node changes
    useEffect(() => {
        const edges = getEdges(store.getState());
        setEdges(highlightConnectedEdges(edges, hoveredNodeId, appStatus));
    }, [hoveredNodeId, appStatus]);

    return { onNodeMouseEnter, onNodeMouseLeave };
};
